// useAuthorization.tsx
import { useEffect } from 'react';
import { useOktaAuth } from "@okta/okta-react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { TokenResponse } from "@okta/okta-auth-js";
import {
  getOktaSettings,
  setAccessToken,
  setAccessTokenExpiry,
  setIdToken,
  setUserSessionExpiry,
} from "./authUtils";
import { fetchUserDetails } from "../slices/userSlice";
import { AppDispatch } from "../store";

export const useAuthorization = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const oktaAuthContext = useOktaAuth();
  const oktaAuth = oktaAuthContext?.oktaAuth;

  useEffect(() => {
    if (import.meta.env.MODE === "development" || import.meta.env.MODE === "localDev" || import.meta.env.MODE === "playwright") {
      return;
    }
    
    const handleAuthorization = async () => {
      const queryParams = new URLSearchParams(window.location.search);
      const code = queryParams.get("code");
      const state = queryParams.get("state");
      const error = queryParams.get("error");
      const errorDescription = queryParams.get("error_description");

      if (state && error && errorDescription) {
        navigate("redirect");
      } else if (code && state) {
        try {
          const tokenResponse: TokenResponse = await oktaAuth!.token.parseFromUrl();
          const { idToken, accessToken } = tokenResponse.tokens;

          if (idToken && accessToken) {
            let userSessionExpiry;

            // Save tokens using Okta's token manager
            oktaAuth!.tokenManager.add("idToken", idToken);
            oktaAuth!.tokenManager.add("accessToken", accessToken);

            // Optionally update your application state (or local storage) with token details
            setAccessToken(accessToken.accessToken);
            setIdToken(idToken.idToken);
            setAccessTokenExpiry(accessToken.expiresAt);

            
            if (accessToken.claims.auth_time) {
              const oktaSettings = getOktaSettings();
              userSessionExpiry =
                accessToken.claims.auth_time +
                Number(oktaSettings.sessionExpiresIn);
              setUserSessionExpiry(userSessionExpiry);
            }
            dispatch(fetchUserDetails({ idToken: idToken.idToken }));
          } else {
            oktaAuth!.signInWithRedirect();
          }
        } catch (error) {
          console.error("Error handling tokens:", error);
          localStorage.setItem("okta-cache-storage", "{}");
          localStorage.setItem("okta-shared-transaction-storage", "{}");
          localStorage.setItem("okta-token-storage", "{}");
          oktaAuth!.signInWithRedirect();
        }
      } else {
        oktaAuth!.signInWithRedirect();
      }
    };

    handleAuthorization();
  }, [dispatch, navigate, oktaAuth]);
};
